const RETRY_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getRetryAfterMs(response) {
  const retryAfter = response.headers.get("retry-after");
  if (!retryAfter) return 0;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const dateValue = Date.parse(retryAfter);
  return Number.isFinite(dateValue) ? Math.max(0, dateValue - Date.now()) : 0;
}

export function formatApiError(error, fallback = "Request failed") {
  const message = String(error?.message || "");

  if (error?.status === 429) {
    return "The dashboard is busy. Please wait a few seconds and try again.";
  }

  if (
    error?.name === "AbortError" ||
    /failed to fetch|networkerror|load failed|timed out|connection/i.test(message)
  ) {
    return "Connection was interrupted. Please try again.";
  }

  return message || fallback;
}

export async function apiRequest(
  url,
  options = {},
  {
    retries = 2,
    timeoutMs = 12000,
    retryStatuses = RETRY_STATUS_CODES,
    parseJson = true
  } = {}
) {
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        cache: "no-store",
        ...options,
        signal: controller.signal
      });
      const data = parseJson ? await response.json().catch(() => ({})) : null;

      if (!response.ok) {
        if (data?.code === "MOBILE_ACCESS_BLOCKED" && options.headers?.Authorization) {
          window.dispatchEvent(new Event("mobile-access-blocked"));
        }
        const error = new Error(data?.message || `Request failed (${response.status})`);
        error.status = response.status;
        error.data = data;
        error.response = response;

        if (attempt < retries && retryStatuses.has(response.status)) {
          const retryAfterMs = getRetryAfterMs(response);
          await delay(retryAfterMs || 350 * (attempt + 1));
          continue;
        }

        throw error;
      }

      return { response, data };
    } catch (error) {
      lastError = error;

      if (error?.status === 401 || error?.status === 403) {
        throw error;
      }

      if (attempt < retries) {
        await delay(350 * (attempt + 1));
        continue;
      }

      throw lastError;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  throw lastError || new Error("Request failed");
}
